/**
 * ParcelasForm - Formulário para definir parcelas de pagamento
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { DialogFooter } from '../ui/dialog';
import { RefreshCw, Loader2, CheckCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from './utils';
import { CurrencyInput } from '../ui/currency-input';

// Função para interpretar condição de pagamento
const interpretarCondicaoPagamento = (condicao) => {
  if (!condicao) return { numParcelas: 1, diasVencimento: [30] };
  
  const texto = condicao.toString().toLowerCase().trim();
  
  // "à vista" ou "a vista" ou "avista"
  if (texto.includes('vista') || texto === 'pix' || texto === 'dinheiro' || texto === 'debito' || texto === 'débito') {
    return { numParcelas: 1, diasVencimento: [0] };
  }
  
  // "entrada" + parcelas: "entrada + 2x 30/60"
  const matchEntrada = texto.match(/entrada\s*\+?\s*(\d+)\s*x/i);
  if (matchEntrada) {
    const parcelas = parseInt(matchEntrada[1]) + 1; // +1 pela entrada
    const diasMatch = texto.match(/\d+/g)?.slice(1); // ignorar primeiro número que é quantidade de parcelas
    if (diasMatch && diasMatch.length >= parcelas - 1) {
      return { 
        numParcelas: parcelas, 
        diasVencimento: [0, ...diasMatch.slice(0, parcelas - 1).map(d => parseInt(d))]
      };
    }
    // Gerar dias automaticamente
    return { 
      numParcelas: parcelas, 
      diasVencimento: [0, ...Array.from({length: parcelas - 1}, (_, i) => (i + 1) * 30)]
    };
  }
  
  // "50% entrada + 50% na entrega"
  if (texto.includes('50%') && texto.includes('entrada')) {
    return { numParcelas: 2, diasVencimento: [0, 30] };
  }
  
  // Número de parcelas com dias específicos: "4x, 30, 60, 90, 120" ou "3x 30/60/90" ou "3x (30/60/90)"
  const matchComParcelasEDias = texto.match(/(\d+)\s*x[\s,()]+(.+)/i);
  if (matchComParcelasEDias) {
    const numParcelas = parseInt(matchComParcelasEDias[1]);
    const restante = matchComParcelasEDias[2];
    const diasMatch = restante.match(/\d+/g);
    if (diasMatch) {
      const dias = diasMatch.map(d => parseInt(d));
      if (dias.length >= numParcelas) {
        return { numParcelas, diasVencimento: dias.slice(0, numParcelas) };
      }
      while (dias.length < numParcelas) {
        const ultimo = dias[dias.length - 1] || 30;
        dias.push(ultimo + 30);
      }
      return { numParcelas, diasVencimento: dias };
    }
    return { 
      numParcelas, 
      diasVencimento: Array.from({length: numParcelas}, (_, i) => (i + 1) * 30)
    };
  }
  
  // Padrão simples no final ou início: "3x" ou "parcelado 3x"
  const matchParcelas = texto.match(/(\d+)\s*x/i);
  if (matchParcelas) {
    const num = parseInt(matchParcelas[1]);
    if (num >= 1 && num <= 48) {
      return { 
        numParcelas: num, 
        diasVencimento: Array.from({length: num}, (_, i) => (i + 1) * 30)
      };
    }
  }
  
  // "30/60/90" ou "30-60-90" ou "30,60,90" ou "30 60 90" (múltiplos dias sem "x")
  const diasMatch = texto.match(/\d+/g);
  if (diasMatch && diasMatch.length > 1) {
    const dias = diasMatch.map(d => parseInt(d)).filter(d => d > 0 && d <= 365);
    if (dias.length > 1) {
      return { numParcelas: dias.length, diasVencimento: dias };
    }
  }
  
  // Número único de dias: "30 dias" ou "30 ddl" ou apenas "30"
  if (diasMatch && diasMatch.length === 1) {
    const dias = parseInt(diasMatch[0]);
    if (dias > 0 && dias <= 365) {
      return { numParcelas: 1, diasVencimento: [dias] };
    }
  }
  
  // Default: 1x em 30 dias
  return { numParcelas: 1, diasVencimento: [30] };
};

const ParcelasForm = ({ pedido, onSave, onCancel }) => {
  const [parcelas, setParcelas] = useState(pedido.parcelas || []);
  const [numParcelas, setNumParcelas] = useState(pedido.parcelas?.length || 1);
  const [loading, setLoading] = useState(false);
  const [observacao, setObservacao] = useState(pedido.parcelas_observacao || '');
  // Snapshot das parcelas no momento da abertura para detectar alterações
  const [parcelasIniciais] = useState(() => JSON.parse(JSON.stringify(pedido.parcelas || [])));
  
  // Interpretar condição de pagamento ao abrir
  const condicaoInterpretada = interpretarCondicaoPagamento(pedido.condicao_pagamento);

  const gerarParcelasAutomaticas = (numParcelas, diasVencimento = null) => {
    if (numParcelas < 1) return [];
    const valorTotal = pedido.total || 0;
    const valorParcela = Math.floor((valorTotal / numParcelas) * 100) / 100;
    const resto = valorTotal - (valorParcela * numParcelas);
    
    const novasParcelas = [];
    const hoje = new Date();
    
    for (let i = 0; i < numParcelas; i++) {
      const dataVencimento = new Date(hoje);
      // Usar dias específicos se fornecidos, senão usar múltiplos de 30
      const diasParaAdicionar = diasVencimento && diasVencimento[i] 
        ? diasVencimento[i] 
        : (i + 1) * 30;
      dataVencimento.setDate(dataVencimento.getDate() + diasParaAdicionar);
      
      novasParcelas.push({
        numero: i + 1,
        valor: i === 0 ? valorParcela + Math.round(resto * 100) / 100 : valorParcela,
        data_vencimento: dataVencimento.toISOString().split('T')[0],
        status: 'pendente'
      });
    }
    return novasParcelas;
  };

  const handleGerarParcelas = () => {
    const novasParcelas = gerarParcelasAutomaticas(numParcelas);
    setParcelas(novasParcelas);
  };
  
  // Gerar parcelas baseado na condição interpretada
  const handleGerarParcelasAutomatico = () => {
    setNumParcelas(condicaoInterpretada.numParcelas);
    const novasParcelas = gerarParcelasAutomaticas(
      condicaoInterpretada.numParcelas, 
      condicaoInterpretada.diasVencimento
    );
    setParcelas(novasParcelas);
    toast.success(`Gerado ${condicaoInterpretada.numParcelas} parcela(s) baseado na condição "${pedido.condicao_pagamento}"`);
  };

  const updateParcela = (index, field, value) => {
    setParcelas(prev => prev.map((p, i) => 
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const somaParcelas = parcelas.reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0);
  const diferenca = (pedido.total || 0) - somaParcelas;

  const handleSave = async () => {
    if (Math.abs(diferenca) > 0.02) {
      toast.error('A soma das parcelas deve ser igual ao total do pedido');
      return;
    }

    // Detectar se houve alteração vs snapshot inicial
    const parcelasComparaveis = (arr) => (arr || []).map(p => ({
      numero: p.numero,
      valor: parseFloat(p.valor) || 0,
      data_vencimento: p.data_vencimento || '',
      forma_pagamento: p.forma_pagamento || '',
    }));
    const houveAlteracao = JSON.stringify(parcelasComparaveis(parcelas)) !== JSON.stringify(parcelasComparaveis(parcelasIniciais));
    const algumaSincronizada = (parcelasIniciais || []).some(p =>
      p.conta_azul_id || p.conta_azul_protocol_id
    );

    // Se houve alteração E já há parcela sincronizada → pergunta o que fazer
    let sincronizar_conta_azul = true;
    if (houveAlteracao && algumaSincronizada) {
      const resposta = window.confirm(
        '⚠️ ATENÇÃO\n\n' +
        'Algumas parcelas já foram enviadas ao Conta Azul anteriormente.\n\n' +
        'Você alterou valores/datas. Como deseja proceder?\n\n' +
        '• OK = Salvar e re-enviar ao Conta Azul (apenas novas parcelas serão criadas — as já sincronizadas NÃO duplicam)\n' +
        '• Cancelar = Salvar apenas no sistema, SEM enviar ao Conta Azul\n\n' +
        'Para atualizar valores no Conta Azul de parcelas já lá, faça manualmente na plataforma.'
      );
      sincronizar_conta_azul = resposta;
    } else if (!houveAlteracao && algumaSincronizada) {
      // Sem alterações + já sincronizadas → não re-enviar (seria no-op de qualquer forma)
      sincronizar_conta_azul = false;
    }

    setLoading(true);
    try {
      await onSave(parcelas, observacao, { sincronizar_conta_azul });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-700">Pedido: <span className="font-mono font-bold">{pedido.numero}</span></p>
            <p className="text-sm text-blue-700">Condição: <span className="font-medium">{pedido.condicao_pagamento || 'Não definida'}</span></p>
            {pedido.forma_pagamento && (
              <p className="text-sm text-blue-700">Forma: <span className="font-medium">{pedido.forma_pagamento}</span></p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-blue-600">Total do Pedido</p>
            <p className="text-xl font-bold text-blue-800">
              {formatCurrency(pedido.total)}
            </p>
          </div>
        </div>
        {/* Botão para gerar automaticamente baseado na condição */}
        {pedido.condicao_pagamento && (
          <div className="mt-3 pt-3 border-t border-blue-200">
            <div className="flex items-center justify-between">
              <p className="text-xs text-blue-600">
                💡 Interpretado: <strong>{condicaoInterpretada.numParcelas}x</strong> 
                {condicaoInterpretada.diasVencimento.length > 1 && 
                  ` (${condicaoInterpretada.diasVencimento.join('/')} dias)`
                }
              </p>
              <Button 
                type="button" 
                size="sm" 
                variant="outline" 
                className="text-blue-700 border-blue-300"
                onClick={handleGerarParcelasAutomatico}
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Aplicar Condição
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-end gap-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex-1">
          <Label>Número de Parcelas</Label>
          <Select value={String(numParcelas)} onValueChange={(v) => setNumParcelas(parseInt(v))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({length: 48}, (_, i) => i + 1).map(n => (
                <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" onClick={handleGerarParcelas}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Gerar Parcelas
        </Button>
      </div>

      {parcelas.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-3 w-16">#</th>
                <th className="text-left p-3">Data de Vencimento</th>
                <th className="text-right p-3">Valor (R$)</th>
                <th className="text-center p-3 w-24">Status</th>
              </tr>
            </thead>
            <tbody>
              {parcelas.map((parcela, idx) => (
                <tr key={idx} className="border-t">
                  <td className="p-3 font-medium">{parcela.numero || idx + 1}</td>
                  <td className="p-3">
                    <Input
                      type="date"
                      value={parcela.data_vencimento || ''}
                      onChange={(e) => updateParcela(idx, 'data_vencimento', e.target.value)}
                      className="h-8"
                    />
                  </td>
                  <td className="p-3">
                    <CurrencyInput
                      value={parcela.valor || 0}
                      onChange={(v) => updateParcela(idx, 'valor', v)}
                      className="h-8 text-right"
                    />
                  </td>
                  <td className="p-3 text-center">
                    <Badge className={
                      parcela.status === 'pago' ? 'bg-green-100 text-green-700' :
                      parcela.status === 'atrasado' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }>
                      {parcela.status === 'pago' ? 'Pago' : 
                       parcela.status === 'atrasado' ? 'Atrasado' : 'Pendente'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr className="border-t-2">
                <td colSpan={2} className="p-3 text-right font-medium">Soma das Parcelas:</td>
                <td className="p-3 text-right font-bold">
                  {formatCurrency(somaParcelas)}
                </td>
                <td className="p-3 text-center">
                  {Math.abs(diferenca) <= 0.02 ? (
                    <CheckCircle className="w-5 h-5 text-green-600 inline" />
                  ) : (
                    <span className="text-red-600 text-xs font-medium">
                      Dif: {formatCurrency(diferenca)}
                    </span>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      
      {/* Campo de Observações */}
      <div>
        <Label>Observações</Label>
        <Textarea
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Ex: NF 12345, Boleto enviado em 01/02, etc."
          rows={2}
          className="mt-1"
        />
        <p className="text-xs text-gray-500 mt-1">Informações úteis como número da NF, boletos, etc.</p>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={loading || parcelas.length === 0}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Salvar Parcelas
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default ParcelasForm;
